import { Link } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Eye, Target } from "lucide-react";
import type { RunSummary } from "../types/history";

interface RunCardProps {
  run: RunSummary;
}

function getSeverityColor(severity: string): string {
  switch (severity) {
    case 'critical':
      return 'bg-red-100 text-red-800 border-red-200';
    case 'moderate':
      return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    case 'minor':
      return 'bg-blue-100 text-blue-800 border-blue-200';
    default:
      return 'bg-gray-100 text-gray-800 border-gray-200';
  }
}

function formatRunTitle(run: RunSummary): string {
  const parts = [];
  if (run.club) parts.push(run.club);
  if (run.swing_type) parts.push(run.swing_type);
  if (run.view) parts.push(`(${run.view})`);
  
  if (parts.length === 0) {
    return "Swing Analysis";
  }
  
  return parts.join(" ");
}

export function RunCard({ run }: RunCardProps) {
  const title = formatRunTitle(run);
  const timeAgo = formatDistanceToNow(new Date(run.timestamp), { addSuffix: true });

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="font-medium text-gray-900">{title}</h3>
            <p className="text-sm text-gray-500 mt-1">{timeAgo}</p>
          </div>
          <Badge variant="secondary" className="ml-2">
            {run.status}
          </Badge>
        </div>
      </CardHeader>
      
      <CardContent className="pt-0">
        {/* Primary Flaws */}
        {run.primary_flaws.length > 0 && (
          <div className="mb-4">
            <div className="flex items-center gap-2 mb-2">
              <Target className="h-4 w-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">Key Areas</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {run.primary_flaws.map((flaw) => (
                <Badge
                  key={flaw.flaw_id}
                  variant="outline"
                  className={getSeverityColor(flaw.severity)}
                >
                  {flaw.title}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Drills Count & Actions */}
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-600">
            {run.drills_count > 0 ? (
              <span>{run.drills_count} drill{run.drills_count === 1 ? '' : 's'} recommended</span>
            ) : (
              <span>No drills available</span>
            )}
          </div>
          
          <Link to={`/analysis/${run.analysis_id}`}>
            <Button variant="outline" size="sm" className="gap-2">
              <Eye className="h-4 w-4" />
              View Details
            </Button>
          </Link>
        </div>
      </CardContent>
    </Card>
  );
}